import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { EditorState, Modifier, RichUtils } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import { insertNewUnstyledBlock } from 'draftjs-utils';
import translations from './translations';

const HtmlEditor = (
  { onChange, value = '', readOnly, disabled, options },
  ref
) => {
  const [editor, setEditor] = useState(EditorState.createEmpty());
  const [loaded, setLoaded] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    if ((readOnly || disabled || !loaded) && value) {
      setLoaded(true);
      setEditor(EditorState.createWithContent(stateFromHTML(value)));
    }
  }, [value]);

  const onEditorStateChange = (editorState) => {
    if (onChange) {
      onChange(stateToHTML(editorState.getCurrentContent()));
    }

    setEditor(editorState);
  };

  const insert = (html) => {
    const currentSelection = editor.getSelection();
    const currentContent = editor.getCurrentContent();

    const newContent = Modifier.replaceText(
      currentContent,
      currentSelection,
      html
    );

    const newEditorState = EditorState.push(
      editor,
      newContent,
      'insert-characters'
    );

    setEditor(
      EditorState.forceSelection(newEditorState, newContent.getSelectionAfter())
    );
  };

  useImperativeHandle(ref, () => ({
    insert
  }));

  return (
    <Editor
      ref={editorRef}
      localization={{
        locale: 'pt-BR',
        translations
      }}
      editorState={editor}
      onEditorStateChange={onEditorStateChange}
      readOnly={readOnly || disabled}
      toolbarHidden={readOnly || disabled}
      handleReturn={(event) => {
        let newEditorState = null;
        if (event.keyCode === 13 && event.shiftKey) {
          newEditorState = insertNewUnstyledBlock(editor);
        } else if (event.keyCode === 13 && !event.shiftKey) {
          newEditorState = RichUtils.insertSoftNewline(editor);
        }
        if (newEditorState) {
          setEditor(newEditorState);
          return true;
        }
        return false;
      }}
      toolbar={{
        options: options ?? [
          'inline',
          'blockType',
          'fontFamily',
          'list',
          'textAlign',
          'colorPicker',
          'link'
        ]
      }}
    />
  );
};

export default React.forwardRef(HtmlEditor);
